import React, { useState, useEffect } from 'react';
import ClockLoader from 'react-spinners/ClockLoader';
import { Typography, Box, Card, CardContent, Grid, Divider } from '@mui/material';

const FieldView = ({ label, value }) => (
  <Grid item xs={12} sm={6} md={3}>
    <Typography variant="caption" color="primary" sx={{ fontWeight: 600, display: 'block', mb: 0.5, fontSize: '0.8rem' }}>
      {label}
    </Typography>
    <Typography variant="body1" sx={{ wordWrap: 'break-word', color: 'text.secondary', fontWeight: 500 }}>
      {value || '-'}
    </Typography>
  </Grid>
);

const SectionCard = ({ title, children }) => (
  <Card variant="outlined" sx={{ mb: 4, borderRadius: 2, borderColor: '#e0e0e0', boxShadow: '0 4px 12px rgba(0,0,0,0.03)' }}>
    <CardContent sx={{ p: { xs: 2, md: 3 } }}>
      <Typography variant="h6" sx={{ color: '#ce690a', fontWeight: 600, mb: 1 }}>
        {title}
      </Typography>
      <Divider sx={{ mb: 3 }} />
      <Grid container spacing={3}>
        {children}
      </Grid>
    </CardContent>
  </Card>
);

export default function Review({ formData, loading }) {
  function DateFormat(dateString) {
    if (!dateString) return '-';
    const date = new Date(dateString);
    if (isNaN(date.getTime())) {
      return dateString;
    }
    const month = new Intl.DateTimeFormat('en-US', { month: 'short' }).format(date);
    const day = new Intl.DateTimeFormat('en-US', { day: 'numeric' }).format(date);
    const year = new Intl.DateTimeFormat('en-US', { year: 'numeric' }).format(date);
    return `${day}-${month}-${year}`;
  }

  function AmountFormat(amount) {
    if (!amount) return '-';
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      minimumFractionDigits: 2,
    }).format(amount);
  }

  const [localData, setLocalData] = useState({
    firstName: '',
    lastName: '',
    phoneNumber: '',
    acn: '',
    dob: '',
    appEmail: '',
    address: '',
    city: '',
    state: '',
    district: '',
    pincode: '',
    usvName: '',
    usvPhone: '',
    usvEmail: '',
    ivName: '',
    ivEmail: '',
    ivPhone: '',
    relationToApplicant: '',
  });

  useEffect(() => {
    const storedDataString = localStorage.getItem('myUsaDatastep1');
    const storedDataVolunteersDetail = localStorage.getItem('volunteersDetail');
    if (storedDataString) {
      const storedData = JSON.parse(storedDataString);
      const volunteersDetails = JSON.parse(storedDataVolunteersDetail);
      setLocalData({
        firstName: storedData.step1.firstName,
        lastName: storedData.step1.lastName,
        phoneNumber: storedData.step1.phoneNumber,
        acn: storedData.step1.aadharNumber,
        dob: storedData.step1.dateOfBirth,
        appEmail: storedData.step1.appEmail,
        address: storedData.step1.addressone,
        city: storedData.step1.city,
        state: storedData.step1.state,
        district: storedData.step1.district,
        pincode: storedData.step1.pincode,
        usvName: volunteersDetails.volunteersDetail.usvname,
        usvPhone: volunteersDetails.volunteersDetail.usvphone,
        usvEmail: volunteersDetails.volunteersDetail.usvemail,
        ivName: volunteersDetails.volunteersDetail.ivname,
        ivEmail: volunteersDetails.volunteersDetail.ivemail,
        ...(volunteersDetails.volunteersDetail.ivphone && { ivPhone: volunteersDetails.volunteersDetail.ivphone }),
        ...(volunteersDetails.volunteersDetail.ivTelephone && { ivPhone: volunteersDetails.volunteersDetail.ivTelephone }),
        relationToApplicant: volunteersDetails.volunteersDetail.relationtoapplicant,
      });
    }
  }, []);

  return (
    <>
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
          <ClockLoader color="#1976d2" loading={loading} size={150} aria-label="Loading Spinner" />
        </Box>
      ) : (
        <Box sx={{ mt: 2, px: { xs: 1, md: 3 } }}>
          <SectionCard title="USA Volunteer Details">
            <FieldView label="Volunteer Name" value={localData.usvName} />
            <FieldView label="Volunteer Email" value={localData.usvEmail} />
            <FieldView label="Volunteer Phone" value={localData.usvPhone} />
          </SectionCard>

          <SectionCard title="Indian Reference Details">
            <FieldView label="Referral Name" value={localData.ivName} />
            <FieldView label="Referral Email" value={localData.ivEmail} />
            <FieldView label="Referral Phone" value={localData.ivPhone} />
            <FieldView label="Relationship with Referral" value={localData.relationToApplicant} />
          </SectionCard>

          <SectionCard title="Applicant Details">
            <FieldView label="First Name" value={formData.firstName || localData.firstName} />
            <FieldView label="Last Name" value={formData.lastName || localData.lastName} />
            <FieldView label="Aadhar Number" value={formData.aadharNumber || localData.acn} />
            <FieldView label="Date of Birth" value={DateFormat(formData.dob || localData.dob)} />
            <FieldView label="Email" value={formData.email || localData.appEmail} />
            <FieldView label="Phone Number" value={formData.phonenumber || localData.phoneNumber} />
            <FieldView label="Address" value={formData.address || localData.address} />
            <FieldView label="City" value={formData.city || localData.city} />
            <FieldView label="State" value={formData.state || localData.state} />
            <FieldView label="District" value={formData.district || localData.district} />
            <FieldView label="Pincode" value={formData.zip || localData.pincode} />
          </SectionCard>

          <SectionCard title="Parent/Guardian Details">
            <FieldView label="Father Name" value={formData.fatherName} />
            <FieldView label="Father Occupation" value={formData.fatherOccupation} />
            <FieldView label="Mother Name" value={formData.motherName} />
            <FieldView label="Mother Occupation" value={formData.motherOccupation} />
            <FieldView label="Family Income" value={AmountFormat(formData.familyIncome)} />
            <FieldView label="Family Members" value={formData.familyCount} />
          </SectionCard>

          <SectionCard title="Aid Request Details">
            <FieldView label="Aid Request Amount" value={AmountFormat(formData.amountRequested)} />
            <FieldView label="Course Type" value={formData.courseType} />
            <FieldView label="Course" value={formData.aidtype} />
            {(formData.courseType === 'professional' || formData.courseType === 'college') && (
              <FieldView label="Final Year of Passing" value={formData.finalYearOfPassing} />
            )}
            <FieldView label="Aid Request Reason" value={formData.reasonForRequesting} />
            <FieldView label="Multiple Year Request" value={formData.multipleyears} />
            {formData.multipleyears === 'yes' && (
              <FieldView label="No. Of. Years Support" value={formData.yearsofsupportisneeded} />
            )}
          </SectionCard>

          <SectionCard title="Institution Details">
            <FieldView label="Institution Name" value={formData.institutionName} />
            <FieldView label="Email" value={formData.institutionEmail} />
            <FieldView label="Mobile Number" value={formData.institutionMobileNumber} />
            <FieldView label="Address" value={formData.institutionAddress} />
            <FieldView label="City" value={formData.institutionCity} />
            <FieldView label="State" value={formData.institutionState} />
            <FieldView label="District" value={formData.institutiondistrict} />
            <FieldView label="Pincode" value={formData.institutionZip} />
            <FieldView label="Beneficiary Name" value={formData.beneficiaryName} />
            <FieldView label="Beneficiary Phone" value={formData.beneficiaryPhone} />
          </SectionCard>

          <SectionCard title="Payment Details">
            <FieldView label="Account Number" value={formData.accountNumber} />
            <FieldView label="IFSC Code" value={formData.ifscCode} />
            <FieldView label="Bank Name" value={formData.bankName} />
            <FieldView label="Branch Name" value={formData.branchName} />
            <FieldView label="Branch Address" value={formData.paymentAddress} />
          </SectionCard>
        </Box>
      )}
    </>
  );
}
